import SMOKING_NODES from 'lucide-nodes:cigarette'
import BREAK_NODES from 'lucide-nodes:coffee'
import MEDICAL_NODES from 'lucide-nodes:cross'
import INTERIOR_DOOR_NODES from 'lucide-nodes:door-closed'
import PROJECT_NODES from 'lucide-nodes:flag'
import FLAME_NODES from 'lucide-nodes:flame'
import INTERIOR_PATH_NODES from 'lucide-nodes:footprints'
import FORK_LIFT_NODES from 'lucide-nodes:forklift'
import HEART_PULSE_NODES from 'lucide-nodes:heart-pulse'
import INFO_NODES from 'lucide-nodes:info'
import LEVEL_NODES from 'lucide-nodes:layers'
import ENTRANCE_NODES from 'lucide-nodes:log-in'
import LOG_OUT_NODES from 'lucide-nodes:log-out'
import GENERIC_LOCATION_NODES from 'lucide-nodes:map-pin'
import ROUTE_NODES from 'lucide-nodes:route'
import HAND_WASH_NODES from 'lucide-nodes:shower-head'
import LINE_STICKER_NODES from 'lucide-nodes:slash'
import SOFA_NODES from 'lucide-nodes:sofa'
import PARKING_NODES from 'lucide-nodes:square-parking'
import TEMPERATURE_NODES from 'lucide-nodes:thermometer'
import TRASH_NODES from 'lucide-nodes:trash-2'
import TRUCK_NODES from 'lucide-nodes:truck'
import TEXT_STICKER_NODES from 'lucide-nodes:type'
import CANTEEN_NODES from 'lucide-nodes:utensils'
import EQUIPMENT_NODES from 'lucide-nodes:wrench'
import ELECTRICAL_ROOM_NODES from 'lucide-nodes:zap'
import type { IconNode, LucideIcon } from 'lucide-react'
import { Slash, Type } from 'lucide-react'

// Not the '~/client/graph' barrel: that module instantiates Apollo and graphql-ws, and this
// file is on the federated LocationIcon's import chain.
import { LocationType } from '~/client/graph/types.generated'
import MapViewLocationIcon, {
  LIBRARY_GLYPHS,
  type LibraryGlyph,
} from '~/client/src/shared/enums/SitemapAttributeIcon'
import {
  AERIAL_LIFT_NODES,
  AREA_NODES,
  BUILDING_NODES,
  CRANE_ALT_NODES,
  CRANE_NODES,
  DUMPSTER_NODES,
  ELEVATOR_NODES,
  GATE_NODES,
  HOIST_NODES,
  MONITORING_NODES,
  MUSTER_POINT_NODES,
  RESTROOM_NODES,
  STAGING_NODES,
  STAIRS_NODES,
  TENT_NODES,
  TRAILER_NODES,
  WALKWAY_NODES,
  ZONE_NODES,
} from '~/client/src/shared/icons'
import {
  hasPendingLibraryGlyphArt,
  isLibraryGlyphArtLoaded,
  LIBRARY_GLYPH_ICONS,
  libraryGlyphArtSettled,
  libraryGlyphNodes,
} from '~/client/src/shared/icons/libraryGlyphs'
import {
  hasPendingPictogramArt,
  isPictogramArtLoaded,
  PICTOGRAM_ICONS,
  PICTOGRAM_VIEW_BOX,
  pictogramArtSettled,
  pictogramNodes,
  PICTOGRAMS,
  pictogramSlugOf,
} from '~/client/src/shared/icons/pictograms'
import type {
  PictogramIconName,
  PictogramSlug,
} from '~/client/src/shared/icons/pictograms'

import { LOCATION_GLYPHS, type StandardLocationIcon } from './locationGlyphs'

// The teardrop workflow pin, shared by the DOM pins, the GL rasterizer and the konva pin so
// the same silhouette cannot drift between them. Drawn on a 20x30 viewBox.
// One shape per glyph: nodes on a 24-unit grid, each carrying its own paint, because the design
// system mixes stroke and fill inside a single icon. A node with a fill is filled, everything
// else stroked. Every consumer reads this table, so it is the only place an icon is chosen.
export interface IIconData {
  nodes: IconNode
  viewBox: string
  // The component form of the same glyph: our own for custom artwork, lucide-react's for
  // standard ones. Held here rather than derived, so a location type has exactly one
  // component and the rasterizer's geometry and the DOM's component cannot diverge.
  Icon: LucideIcon
  // Full-colour art that paints its own fills and is the whole marker: no disc, no ink.
  isPictogram?: true
}

export const ICON_VIEW_BOX = '0 0 24 24'

// Composed here rather than in the icon lib: the lib exports geometry and a plain Lucide
// component and stays unaware of who needs which. A DOM consumer imports the component; the
// canvas rasterizer needs the nodes, and this is the only place both are wanted at once.
// The pairing is asserted in iconGeometry.test.
const glyph = (nodes: IconNode, Icon: LucideIcon): IIconData => ({
  viewBox: ICON_VIEW_BOX,
  nodes,
  Icon,
})

// A library glyph's art loads on demand, so `nodes` is read through each time: nothing until the
// art lands, then the art. Read once per draw, and inside an observer to redraw when it lands.
const libraryGlyph = (name: LibraryGlyph): IIconData => ({
  viewBox: ICON_VIEW_BOX,
  get nodes() {
    return libraryGlyphNodes(name)
  },
  Icon: LIBRARY_GLYPH_ICONS[name],
})

// Line and TextBox are drawing primitives, not location types — they carry no
// iconProperties and are handled by the sitemapItem branch of SitemapAttributeIcon.
export type LocationTypeIcon = Exclude<
  MapViewLocationIcon,
  MapViewLocationIcon.Line | MapViewLocationIcon.TextBox
>

// Total rather than Partial on purpose, like LOCATION_GLYPHS: adding an enum member without
// artwork must fail typecheck instead of silently falling back at runtime. The component half
// lives in locationGlyphs so field glyphs can read it without this module's art loaders.
const LOCATION_NODES: Record<StandardLocationIcon, IconNode> = {
  [MapViewLocationIcon.Gate]: GATE_NODES,
  [MapViewLocationIcon.Zone]: ZONE_NODES,
  [MapViewLocationIcon.Building]: BUILDING_NODES,
  [MapViewLocationIcon.Route]: ROUTE_NODES,
  [MapViewLocationIcon.Equipment]: EQUIPMENT_NODES,
  [MapViewLocationIcon.Level]: LEVEL_NODES,
  [MapViewLocationIcon.Area]: AREA_NODES,
  [MapViewLocationIcon.Logistics]: GENERIC_LOCATION_NODES,
  [MapViewLocationIcon.Bathroom]: RESTROOM_NODES,
  [MapViewLocationIcon.Canteen]: CANTEEN_NODES,
  [MapViewLocationIcon.Break]: BREAK_NODES,
  [MapViewLocationIcon.Dumpster]: DUMPSTER_NODES,
  [MapViewLocationIcon.Elevator]: ELEVATOR_NODES,
  [MapViewLocationIcon.Entrance]: ENTRANCE_NODES,
  [MapViewLocationIcon.HandWash]: HAND_WASH_NODES,
  [MapViewLocationIcon.Medical]: MEDICAL_NODES,
  [MapViewLocationIcon.MeetingPoint]: MUSTER_POINT_NODES,
  [MapViewLocationIcon.Parking]: PARKING_NODES,
  [MapViewLocationIcon.Smoking]: SMOKING_NODES,
  [MapViewLocationIcon.Stairs]: STAIRS_NODES,
  [MapViewLocationIcon.Shaft]: HOIST_NODES,
  [MapViewLocationIcon.Temperature]: TEMPERATURE_NODES,
  [MapViewLocationIcon.Tent]: TENT_NODES,
  [MapViewLocationIcon.Toilet]: RESTROOM_NODES,
  [MapViewLocationIcon.Walkway]: WALKWAY_NODES,
  [MapViewLocationIcon.ElectricalRoom]: ELECTRICAL_ROOM_NODES,
  [MapViewLocationIcon.Trailer]: TRAILER_NODES,
  [MapViewLocationIcon.Crane]: CRANE_NODES,
  [MapViewLocationIcon.Hoist]: HOIST_NODES,
  [MapViewLocationIcon.AerialLift]: AERIAL_LIFT_NODES,
  [MapViewLocationIcon.Gradall]: CRANE_ALT_NODES,
  [MapViewLocationIcon.ForkLift]: FORK_LIFT_NODES,
  [MapViewLocationIcon.Monitoring]: MONITORING_NODES,
  [MapViewLocationIcon.Staging]: STAGING_NODES,
  [MapViewLocationIcon.InteriorDoor]: INTERIOR_DOOR_NODES,
  [MapViewLocationIcon.InteriorPath]: INTERIOR_PATH_NODES,
  [MapViewLocationIcon.Project]: PROJECT_NODES,
  [MapViewLocationIcon.Truck]: TRUCK_NODES,
  [MapViewLocationIcon.Info]: INFO_NODES,
  [MapViewLocationIcon.LogOut]: LOG_OUT_NODES,
  [MapViewLocationIcon.Trash]: TRASH_NODES,
  [MapViewLocationIcon.Sofa]: SOFA_NODES,
  [MapViewLocationIcon.HeartPulse]: HEART_PULSE_NODES,
  [MapViewLocationIcon.Flame]: FLAME_NODES,
}

export const ICON_DATA: Record<LocationTypeIcon, IIconData> = {
  ...(Object.fromEntries(
    (Object.keys(LOCATION_NODES) as StandardLocationIcon[]).map(icon => [
      icon,
      glyph(LOCATION_NODES[icon], LOCATION_GLYPHS[icon]),
    ]),
  ) as Record<StandardLocationIcon, IIconData>),
  ...(Object.fromEntries(
    LIBRARY_GLYPHS.map(name => [name, libraryGlyph(name)]),
  ) as Record<LibraryGlyph, IIconData>),
}

// The component form of the same table, derived rather than hand-listed for the same
// reason: a location type can't end up with artwork on one surface and not the other.
// Names come from the enum, so the rendered class reads lucide-<location type>.
export const LOCATION_ICON_COMPONENTS = Object.fromEntries(
  (Object.keys(ICON_DATA) as LocationTypeIcon[]).map(icon => [
    icon,
    ICON_DATA[icon].Icon,
  ]),
) as Record<LocationTypeIcon, LucideIcon>

export type StickerIcon = MapViewLocationIcon.Line | MapViewLocationIcon.TextBox

// The two members ICON_DATA excludes. They have to stay out of it — a sticker draws no marker,
// and a total ICON_DATA is what the rasterizer and the konva guard key off — but they still need
// the nodes/component pairing, or every surface invents its own artwork for them: the pallet
// button, the tag icon and the placement cursor each had their own copy.
export const STICKER_ICON_DATA: Record<StickerIcon, IIconData> = {
  [MapViewLocationIcon.TextBox]: glyph(TEXT_STICKER_NODES, Type),
  [MapViewLocationIcon.Line]: glyph(LINE_STICKER_NODES, Slash),
}

// Whatever holds a bare iconName resolves through here rather than picking a table.
export const getGlyph = (icon: string): IIconData =>
  ICON_DATA[icon as LocationTypeIcon] ?? STICKER_ICON_DATA[icon as StickerIcon]

// A sticker is icon-less by nature — it draws its own shape and no marker — so a surface that
// draws marker chrome has to skip it rather than take getMarkerGlyph's pin fallback.
export const isStickerIcon = (iconName: string): boolean =>
  iconName in STICKER_ICON_DATA

// A sign's art loads on demand, so `nodes` is read through each time: its tone tile until the art
// lands, then the art. Read once per draw, and inside an observer to redraw when it lands.
const pictogram = (slug: PictogramSlug): IIconData => ({
  viewBox: PICTOGRAM_VIEW_BOX,
  get nodes() {
    return pictogramNodes(slug)
  },
  Icon: PICTOGRAM_ICONS[slug],
  isPictogram: true,
})

const PICTOGRAM_GLYPHS = Object.fromEntries(
  PICTOGRAMS.map(({ slug }) => [slug, pictogram(slug)]),
) as Record<PictogramSlug, IIconData>

// What a location can be drawn with: a StruxHub glyph or a library sign.
export type LocationIconName = MapViewLocationIcon | PictogramIconName

// A location's custom icon, stored as `pictogram:<slug>`. Undefined for anything else, including
// a slug the catalog doesn't know.
export const getPictogramGlyph = (iconName: string): IIconData | undefined => {
  const slug = pictogramSlugOf(iconName)
  return slug ? PICTOGRAM_GLYPHS[slug] : undefined
}

const LIBRARY_GLYPH_NAMES: ReadonlySet<string> = new Set(LIBRARY_GLYPHS)

// Whether an icon's art is drawn yet: a sign or a library glyph loads on demand, everything else
// ships with the app. Asking starts the load; read inside an observer to react when it lands.
export function isIconArtLoaded(iconName: string): boolean {
  const slug = pictogramSlugOf(iconName)
  if (slug) return isPictogramArtLoaded(slug)
  return LIBRARY_GLYPH_NAMES.has(iconName)
    ? isLibraryGlyphArtLoaded(iconName as LibraryGlyph)
    : true
}

export const hasPendingIconArt = (): boolean =>
  hasPendingPictogramArt() || hasPendingLibraryGlyphArt()

/** Settles once every icon-art load already started has, so a capture never bakes in a stand-in. */
export const iconArtSettled = (): Promise<void> =>
  Promise.all([pictogramArtSettled(), libraryGlyphArtSettled()]).then(
    () => undefined,
  )

// Always a glyph: iconName arrives from the DTO as a bare string, so an unrecognised value takes
// the pin rather than leaving the location invisible. Icon-less items carry no iconProperties and
// never reach here.
export function getMarkerGlyph(iconName: string): IIconData {
  return (
    ICON_DATA[iconName as LocationTypeIcon] ??
    getPictogramGlyph(iconName) ??
    ICON_DATA[MapViewLocationIcon.Logistics]
  )
}

// iconName reaches the UI as a bare string in a dozen places, and three live values
// ('map-marker', 'sensor', 'material') are not enum members at all — so the unknown case
// is resolved here once rather than by each renderer inventing a fallback.
// LocationType -> its glyph key. Lives beside ICON_DATA so the factory, the placement
// cursor and anything else resolving a type's artwork read one table.
// Total, not Partial: a new LocationType has to declare its glyph here — or declare that it has
// none — instead of silently falling back at runtime, which is the same guarantee ICON_DATA buys.
// The resource types are the only null entries; they draw no location glyph.
export const LOCATION_ICON_BY_TYPE: Record<
  LocationType,
  LocationTypeIcon | null
> = {
  [LocationType.Building]: MapViewLocationIcon.Building,
  [LocationType.Zone]: MapViewLocationIcon.Zone,
  [LocationType.Route]: MapViewLocationIcon.Route,
  [LocationType.Gate]: MapViewLocationIcon.Gate,
  [LocationType.OffloadingEquipment]: MapViewLocationIcon.Equipment,
  [LocationType.LogisticsObject]: MapViewLocationIcon.Logistics,
  [LocationType.VerticalObject]: MapViewLocationIcon.Stairs,
  [LocationType.Level]: MapViewLocationIcon.Level,
  [LocationType.Area]: MapViewLocationIcon.Area,
  [LocationType.Integration]: MapViewLocationIcon.Monitoring,
  [LocationType.Staging]: MapViewLocationIcon.Staging,
  [LocationType.InteriorDoor]: MapViewLocationIcon.InteriorDoor,
  [LocationType.InteriorPath]: MapViewLocationIcon.InteriorPath,
  [LocationType.ProjectLocation]: MapViewLocationIcon.Project,
  [LocationType.Material]: null,
  [LocationType.Sensor]: null,
  // A collapsed location draws a glyph, but not one the enum can name: its kind lives in
  // data.type, so the artwork is resolved from that via getLocationKind rather than here
  // (STX1-597). Null keeps the table total without inventing a glyph for "any location".
  [LocationType.Location]: null,
}

export const getLocationIconComponent = (iconName: string): LucideIcon =>
  getMarkerGlyph(iconName).Icon

export const SORT_KEY_BY_TYPE: Partial<Record<MapViewLocationIcon, number>> = {
  [MapViewLocationIcon.Building]: 1,
  [MapViewLocationIcon.Zone]: 2,
  [MapViewLocationIcon.Gate]: 3,
  [MapViewLocationIcon.Route]: 4,
  [MapViewLocationIcon.Equipment]: 5,
  [MapViewLocationIcon.Level]: 6,
  [MapViewLocationIcon.Area]: 7,
  [MapViewLocationIcon.Staging]: 8,
  [MapViewLocationIcon.InteriorDoor]: 15,
  [MapViewLocationIcon.InteriorPath]: 15,
}

export const DEFAULT_SORT_KEY = 10
